/**
 * Thin wrapper over the global Intercom widget: the bits of its behaviour that
 * have to be discovered rather than called.
 *
 * Nothing here holds state. The rules it encodes were all found by observation
 * against the live widget, so each is commented with what breaks without it.
 */

declare global {
  interface Window {
    /**
     * Defined synchronously by the loader snippet in index.html, first as a
     * queueing stub and then as the real SDK once widget.intercom.io answers.
     */
    Intercom?: ((method: string, ...args: unknown[]) => void) & {
      q?: unknown[];
    };
  }
}

/** Calls the widget if it is there. It never is on a blocked page. */
export function callIntercom(method: string, ...args: unknown[]) {
  window.Intercom?.(method, ...args);
}

export function isIntercomBlocked(): boolean {
  const intercom = window.Intercom;
  // The loader stub carries the queue it drains on load; the real SDK has no
  // `q`, so this is only true while the script has not arrived.
  return typeof intercom === "function" && Array.isArray(intercom.q);
}

/**
 * Intercom sets `intercom-session-<app_id>` once a session is established, so
 * its presence means the messenger has already identified this user on an
 * earlier page and may be restoring their conversation right now. Booting over
 * that closes the panel, so attributes go through `update` instead.
 */
export function hasIntercomSession(): boolean {
  return document.cookie.includes("intercom-session-");
}

/**
 * Whether the messenger panel is currently up, read from the DOM.
 *
 * The `messengerOpen` flag cannot answer this on a fresh load: Intercom
 * restores a conversation that was open when the page unloaded without firing
 * `onShow`, so the flag stays false while the panel is plainly there.
 *
 * Presence is not the test. A closed messenger keeps its element — observed
 * live as `0x0` with `visibility: hidden` — so asking only whether it exists
 * reports "open" for the rest of the session.
 */
export function isMessengerPanelOpen(): boolean {
  const panel = document.querySelector(
    '.intercom-messenger-frame, [class*="intercom-messenger"]',
  );
  if (!(panel instanceof HTMLElement)) return false;
  const { width, height } = panel.getBoundingClientRect();
  return (
    width > 0 && height > 0 && getComputedStyle(panel).visibility !== "hidden"
  );
}

const READY_POLL_MS = 200;

/**
 * How long to keep waiting before treating the widget as never arriving.
 *
 * The default is generous because giving up early means reporting a blocked
 * messenger that is merely slow. Callers that owe the user quicker feedback —
 * a click that has to say something — pass a shorter one.
 */
export const READY_TIMEOUT_MS = 20_000;

/**
 * Runs `callback` once the widget script has replaced the loader stub.
 *
 * Calls made against the stub are queued rather than dropped, but the queue is
 * drained around the boot that `window.intercomSettings` triggers, so anything
 * setting messenger state loses to that boot and is silently discarded. React
 * mounts well before `window.onload`, which is when index.html requests the
 * script, so this is the normal case on a cold load, not a rare race.
 *
 * Gives up after `timeout` and runs the callback anyway: if the script is
 * blocked outright the call is a harmless no-op on the stub, and the fallback
 * in useIntercom is what surfaces that to the user. A caller that needs to
 * tell blocked from merely slow re-checks `isIntercomBlocked()` inside the
 * callback.
 *
 * Returns a canceller, so a caller that unmounts stops polling.
 */
export function whenIntercomReady(
  callback: () => void,
  timeout: number = READY_TIMEOUT_MS,
) {
  if (!isIntercomBlocked()) {
    callback();
    return () => {};
  }
  const startedAt = Date.now();
  const pollId = setInterval(() => {
    if (isIntercomBlocked() && Date.now() - startedAt < timeout) {
      return;
    }
    clearInterval(pollId);
    callback();
  }, READY_POLL_MS);
  return () => clearInterval(pollId);
}

export const intercomReady = () =>
  new Promise<void>((resolve) => whenIntercomReady(resolve));
